<template>
  <div
    v-if="visibleBanners.length"
    ref="bannersRef"
    class="homepage-banners">
    <BaseBanner
      v-for="banner in visibleBanners"
      :key="banner.id"
      type="default-center"
      :show-icon="false"
      :show-close-button="true"
      @close="dismissBanner(banner.id)">
      <template #text>
        <div
          class="banner-content"
          v-html="parseMarkdown(banner.text, true)"/>
      </template>
    </BaseBanner>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted, watch } from 'vue'
import { useElementBounding, useEventListener } from '@vueuse/core'
import { useGenericContentStore } from '@shared/stores/genericContent.js'
import { parseMarkdown } from '@/utils/stringParsing.js'
import BaseBanner from '@/components/headers/BaseBanner.vue'

const store = useGenericContentStore()
const dismissedBannerIds = ref([])
const bannersRef = ref(null)

const { height: bannerHeight, bottom: bannerBottom } = useElementBounding(bannersRef)

function updateCssVariables () {
  if (!bannersRef.value) {
    document.documentElement.style.setProperty('--banner-height', '0px')
    document.documentElement.style.setProperty('--navbar-top', '0px')
    return
  }
  document.documentElement.style.setProperty('--banner-height', `${bannerHeight.value}px`)
  document.documentElement.style.setProperty('--navbar-top', bannerBottom.value <= 0 ? '0px' : `${bannerBottom.value}px`)
}

watch([bannerHeight, bannerBottom], updateCssVariables, { immediate: true })

useEventListener(window, 'scroll', updateCssVariables, { passive: true })

onUnmounted(() => {
  document.documentElement.style.setProperty('--banner-height', '0px')
  document.documentElement.style.setProperty('--navbar-top', '0px')
})

const banners = computed(() => {
  const section = store.homepageBannersSection
  return section?.blocks ?? []
})

const visibleBanners = computed(() => {
  return banners.value.filter(banner => !dismissedBannerIds.value.includes(banner.id))
})

watch(visibleBanners, updateCssVariables)

function dismissBanner (bannerId) {
  dismissedBannerIds.value.push(bannerId)
}
</script>

<style lang="scss" scoped>
@import '@shared/assets/global.scss';

.homepage-banners {
  display: flex;
  flex-direction: column;
  gap: 1px;
  position: relative;
  z-index: $zindex-offcanvas + 1;

  :deep(> div) {
    min-height: 36px;
  }
}

.banner-content {
  :deep(a) {
    color: inherit;
    text-decoration: underline;

    &:hover {
      opacity: 0.9;
    }
  }

  :deep(strong),
  :deep(b) {
    font-weight: 600;
  }
}
</style>

